// Экран «Списки» — первый экран нового фронта (п.20, 2026-09-07).
// Настраиваемые списки, контуры (теги работ) и справка по логикам монтажа.
// Логики с 2026-09-10 системные (решение владельца): показываем название,
// описание правила и примеры, править и заводить нельзя. Правки списков
// скрыты без права планировать.
import { useCallback, useEffect, useState } from 'react';
import { ask } from '../dialog';
import { call, p, type Data, type Req } from '../api';
import type { ShellCtx } from '../shell';

type Spisok = Data<'/api/projects/{pid}/spiski', 'get'>[number];
type Logika = Data<'/api/projects/{pid}/logiki', 'get'>[number];

const SP_INFO: [string, string, string][] = [
  ['mesto', 'Место работ', 'Где выполняется работа (квартиры, МОП, паркинг…) — для удобства фильтрации и группировки работ.'],
  // «Тип этажа» с 2026-09-12 не справочник, а системный набор (util.TIPY_ETAZHA)
  // «Статус пуска тепла» убран 2026-09-12: его заменили дата и отметка «случилось» у этажа
  ['etap', 'Этап', 'Этап строительства у работы — для группировки в сводных и отчётах.'],
  ['gruppa', 'Группы работ', 'Группа работ у работы (инженерная система: водоснабжение, кондиционирование…) — колонка на «Работах», фильтр «Группа работ» в модели (2026-09-13).'],
  ['resurs', 'Ресурс', 'Названия ресурсов (бригад, звеньев) — назначаются работам.'],
  // «Подрядчики» с 2026-09-14 — отдельный справочник (экран «Подрядчики»); здесь только показ
  ['ed_izm', 'Единицы измерения', 'Единицы измерения работ (м², шт., компл. …) — колонка «Ед. изм.» на «Работах». Справочная, в расчёте не участвует.'],
  ['kontur', 'Контуры', 'Тег работы для фильтров и сводок (Фасад, Паркинг, Слаботочные сети…). На создание задач не влияет — это задаёт «Раскатка» на «Работах». Клик по тегу — переименовать.'],
  // «Отметки в шахматке» и показ «Подрядчиков» убраны с экрана владельцем 2026-09-15:
  // отметки — встроенные («Материал на площадке», «Замечания»), подрядчики — свой экран
];

export function Spiski({ ctx }: { ctx: ShellCtx }) {
  const [spiski, setSpiski] = useState<Spisok[]>([]);
  const [logiki, setLogiki] = useState<Logika[]>([]);
  const [err, setErr] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    const [s, l] = await Promise.all([
      call<Spisok[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
      call<Logika[]>('get', p('/api/projects/{pid}/logiki', { pid: ctx.pid })),
    ]);
    if (!s.ok) { setErr(s.error.message); return; }
    if (!l.ok) { setErr(l.error.message); return; }
    setSpiski(s.data); setLogiki(l.data); setErr('');
  }, [ctx.pid]);
  useEffect(() => { void load(); }, [load]);

  const changed = () => { ctx.onChanged?.(); void load(); };
  const fail = (m: string) => { setErr(m); ctx.onError?.(m); };

  async function addSp(kod: string) {
    const zn = (drafts[kod] || '').trim();
    if (!zn) return;
    const body: Req<'/api/projects/{pid}/spiski', 'post'> = { kod, znachenie: zn };
    const r = await call('post', p('/api/projects/{pid}/spiski', { pid: ctx.pid }), body);
    if (!r.ok) { fail(r.error.message); return; }
    setDrafts(d => ({ ...d, [kod]: '' }));
    changed();
  }
  async function delSp(id: number) {
    const r = await call('delete', p('/api/spiski/{sid}', { sid: id }));
    if (!r.ok) { fail(r.error.message); return; }
    changed();
  }
  async function renameKontur(x: Spisok) {
    const nn = (await ask('Название контура:', x.znachenie) || '').trim();
    if (!nn || nn === x.znachenie) return;
    const r = await call('patch', p('/api/spiski/{sid}', { sid: x.id }), { znachenie: nn });
    if (!r.ok) { fail(r.error.message); return; }
    changed();
  }

  const byKod = (kod: string) => spiski.filter(x => x.kod === kod);
  const can = ctx.canPlan;

  return (
    <div>
      <div className="pw-err">{err}</div>
      <div className="pw-grid">
        {SP_INFO.map(([kod, title, hint]) => (
          <div className="pw-card" key={kod}>
            <h3 title={hint}>{title}</h3>
            <div className="pw-tags">
              {byKod(kod).map(x => (
                <span className="pw-tag" key={x.id}>
                  <span className={can && kod === 'kontur' ? 'pw-link' : ''}
                    title={can && kod === 'kontur' ? 'Клик — переименовать контур' : undefined}
                    onClick={() => { if (can && kod === 'kontur') void renameKontur(x); }}>{x.znachenie}</span>
                  {can && <button title="удалить" onClick={() => void delSp(x.id)}>×</button>}
                </span>
              ))}
            </div>
            {can && (
              <div className="pw-row">
                <input placeholder="новое значение" value={drafts[kod] || ''}
                  onChange={e => setDrafts(d => ({ ...d, [kod]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') void addSp(kod); }} />
                <button className="sec" onClick={() => void addSp(kod)}>Добавить</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="pw-section">Логики монтажа</div>
      {/* фиксированная разметка: описания длинные, иначе таблица уезжает за экран */}
      <table className="pw-table" style={{ marginTop: 8, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '15%' }} /><col style={{ width: '50%' }} />
          <col style={{ width: '28%' }} /><col style={{ width: '7%' }} />
        </colgroup>
        <thead><tr>
          <th>Название</th><th>Описание правила</th><th>Примеры работ</th>
          <th title="Сколько работ проекта планируется по этой логике">Работ</th>
        </tr></thead>
        <tbody>
          {logiki.map(l => (
            <tr key={l.id}>
              <td>{l.name}</td>
              <td style={{ whiteSpace: 'normal' }}>{l.opisanie}</td>
              <td style={{ whiteSpace: 'normal' }}>{l.primery}</td>
              <td className="num">{l.rabot}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pw-muted" style={{ marginTop: 8 }}>
        Логики монтажа <b>системные</b>: набор задан программой, менять и заводить свои не нужно — правило очереди
        внутри каждой описано выше. Логика выбирается работе в справочнике «Работы», после смены — «Пересчитать».{' '}
        <b>Работа без логики</b> считается как «Типовые снизу вверх» — базовая логика планирования.
      </div>
      <div className="pw-muted" style={{ marginTop: 8 }}>
        <b>Как строится очередь</b> — коротко: задачи одной работы и бригады в корпусе идут одна за другой; порядок
        задаёт логика, а внутри неё — фиксация датами (факт и ручные даты — вперёд) и приоритет этажа. Разбор по шагам —
        в справке «Как считаются сроки» (пункт меню слева).
      </div>
    </div>
  );
}
